import { useEffect, useRef } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { ConfigTabs } from 'src/pages/Settings/memberships/components/config-tabs';
import type { PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import {
  type ConfigFormSaveSlots,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
} from 'src/pages/Settings/memberships/types';

export type MembershipsConfigViewFormProps = ConfigFormSaveSlots<PlansTabFormValues> & {
  data: PlansTabFormValues;
  readOnly: boolean;
  /** Called with current form values on every change so the preview can update live. */
  onFormValuesChange?: (values: PlansTabFormValues) => void;
  /** True while a save's refetches are in flight; suppresses re-seeding from partial data. */
  isApplyingSave?: boolean;
};

export const MembershipsConfigViewForm = ({
  data,
  readOnly,
  onSaveRequestChange,
  onDirtyChange,
  onSubmitStateChange,
  onFormValuesChange,
  isApplyingSave = false,
}: MembershipsConfigViewFormProps) => {
  const formMethods = useForm<PlansTabFormValues>({
    defaultValues: data,
    mode: 'onChange',
  });
  const { isDirty, isValid, isValidating, isSubmitting } = useFormState({
    control: formMethods.control,
  });

  // Report current form values so the preview can update immediately on every change.
  useEffect(() => {
    onFormValuesChange?.(formMethods.getValues());
    const { unsubscribe } = formMethods.watch((values) => {
      onFormValuesChange?.(values as PlansTabFormValues);
    });
    return () => {
      unsubscribe();
    };
  }, [formMethods, onFormValuesChange]);

  // useForm only seeds from `data` at mount, so re-seed when the server data actually changes —
  // after a save that is the refetched config, carrying a new plan's real partnerProductId.
  // Skipped while dirty so live edits survive; the identity check keeps a dirty->clean transition
  // from re-seeding stale data, and leaves the pending change to apply once the edits are saved.
  const seededData = useRef(data);

  useEffect(() => {
    if (isApplyingSave || seededData.current === data || isDirty) {
      return;
    }

    // The config query is `no-cache`, so `data` is null while it refetches and the layout
    // substitutes DEFAULT_PLANS_TAB_VALUES — and the two queries settle independently. Re-seeding
    // from a snapshot with fewer plans than the form holds would empty the form and the preview
    // until the next update lands. Wait for one that is at least as complete.
    if (data.plans.length < (formMethods.getValues().plans?.length ?? 0)) {
      return;
    }

    seededData.current = data;
    formMethods.reset(data);
  }, [data, formMethods, isApplyingSave, isDirty]);

  useEffect(() => {
    return () => {
      onSaveRequestChange(null);
      onDirtyChange(false);
      onSubmitStateChange(EMPTY_CONFIG_FORM_SUBMIT_STATE);
    };
  }, [onSaveRequestChange, onDirtyChange, onSubmitStateChange]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSubmitStateChange({ isValid, isValidating, isSubmitting });
  }, [isSubmitting, isValid, isValidating, onSubmitStateChange]);

  useEffect(() => {
    onSaveRequestChange(() => async () => {
      const ok = await formMethods.trigger();
      if (!ok) {
        return null;
      }

      const values = formMethods.getValues();

      return {
        values,
        reset: () => formMethods.reset(values),
      };
    });
  }, [formMethods, onSaveRequestChange]);

  return (
    <GenericForm<PlansTabFormValues> formMethods={formMethods} gap={3}>
      <ConfigTabs readOnly={readOnly} />
    </GenericForm>
  );
};
